import cleanObjectsEmptyProperties from 'utils/cleanObject';

const nhsTrackingId = "UA-40843711-4";

/**
 * @private getCentralStandardTime: get Central Standard Time (Austin time).
 *
 */
function getCentralStandardTime() {
  // Get current time
  const date = new Date();

  // The difference, in minutes, between the date as evaluated in the UTC time zone and
  // as evaluated in the local time zone. The actual local time algorithm is implementation-defined,
  // and the return value is allowed to be zero in runtimes without appropriate data.
  const browsertTimeZone = date.getTimezoneOffset();

  // convert browsertTimeZone to milliseconds
  // get UTC time in milliseconds
  const utc = date.getTime() + browsertTimeZone * 60000;

  //  Get the number of milliseconds of central standard time.
  // 3600000 is the conversion of hours to milliseconds
  // -5 is the GMT-5 time zone of austin. We just need to multply the value.
  // 3600000 * -5 = -18000000 milliseconds
  const centralStandardTimeZone = -18000000;

  // Calculate the new date with the central standard timezone.
  const now = new Date(utc + centralStandardTimeZone);

  return (
    `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}` +
    `T${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}.${now.getMilliseconds()}-05:00`
  );
}

function getResultCount() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "matchingResults"
  );
}

function getFiltersApplied() {
  const filtersApplied =
    window.NewHomeSource.Modules.tagManager.getFromDataLayer("filtersApplied");
  if (!filtersApplied) {
    return undefined;
  }

  const filters = JSON.parse(filtersApplied);
  const propNames = Object.getOwnPropertyNames(filters);
  for (let i = 0; i < propNames.length; i += 1) {
    const propName = propNames[i];
    if (
      filters[propName] === null ||
      filters[propName] === false ||
      filters[propName] === 0 ||
      filters[propName] === ""
    ) {
      delete filters[propName];
    }
  }
  return filters;
}

function getPageType() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("pageType");
}

function getSegmentAnonymousId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "segmentAnonymousID"
  );
}

function getBrandName() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("brandName");
}

function getBrandId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("brandId");
}

function getCurrentPageNumber() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "currentPageNumber"
  );
}

function getresponseVersion() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "adaptiveTemplate"
  );
}

function getPartnerId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("partnerId");
}

function getPartnerName() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "partnerName"
  );
}

function getPriceReduction() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "priceReduction"
  );
}

function getBrandPartnerName() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "brandPartnerName"
  );
}

function getBrandPartnerNamePartnerName() {
  return `${getBrandPartnerName()}-${getPartnerName()}`;
}

function getCommunityId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "communityId"
  );
}

function getCommunityType() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "communityType"
  );
}

/**
 * @private
 * getMarketId: get the market id of the current page.
 * The difference between @see {@link getUserMarketId} is that getMarketId gets the
 * market id depending on the page search by the user, and @see {@link getUserMarketId}
 * gets the market id accoding the real location (IP address) of the user.
 *
 * @returns market id from dataLayer.
 */
function getMarketId() {
  return (
    window.NewHomeSource.Modules.tagManager.getFromDataLayer("marketId") || ""
  );
}

function getMarketName() {
  return (
    window.NewHomeSource.Modules.tagManager.getFromDataLayer("marketName") || ""
  );
}

function getCodeBuildVersion() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "buildCodeVersion"
  );
}

function getAbTestsOnThePage() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "AbTestOnThisPage"
  );
}

function getBuilderId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("builderId");
}

function getBuilderDivision() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "builderDivision"
  );
}

function getBuilderName() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "builderCorp"
  );
}

function getCommunityName() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "communityName"
  );
}

function getOffMarket() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("OffMarket");
}

function getPrimaryImageUrl() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "PrimaryImage"
  );
}

function getLowPrice() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("PriceLow");
}

/**
 * @private hasIfp: get a value indicating if it has interactive floor plans images.
 *
 * @returns {boolean} indicating it has interactive floor plans images.
 */
function hasIfp() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("hasIfp");
}

/**
 * @private hasIsp: get a value indicating if it has interactive site plans images.
 *
 * @returns {boolean} indicating it has interactive site plans images.
 */
function hasIsp() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("hasIsp");
}

function getHomePrice() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("HomePrice");
}

function getHighPrice() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("PriceHigh");
}

/**
 * @private isLimitedAvailability: get a value indicating if it is limited availability.
 *
 * @returns {boolean} indicating it is limited availability.
 */
function isLimitedAvailability() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "isLimitedAvailability"
  );
}

/**
 * @private isPriced: get a value indicating if it has priced.
 *
 * @returns {boolean} indicating has priced.
 */
function isPriced() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("isPriced");
}

function getCity() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("city");
}

function getCounty() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("County");
}

function getIUid() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("i_uid");
}

function getState() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("State");
}

function getZip() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("ZipCode");
}

function getPlanId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("planId");
}

function getPlanName() {  
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("planName");  
}

function getSpecId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("specId");
}

function getHomeType() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("homeStatus");
}

/**
 * @private getLocation: get the the city, state.
 *
 * @returns A string that contains {City} - {State}.
 */
function getLocation() {
  return (
    window.NewHomeSource.Modules.tagManager.getFromDataLayer("location") || ""
  );
}

function getUserId() {
  const userId = window.NewHomeSource.Modules.cookieHandler.readCookie(
    "Profile",
    true
  );

  if (userId) {
    return userId.replace("{", "").replace("}", "");
  }

  return userId;
}

function getGoogleAnalyticsClientId() {
  let googleAnalyticsId;

  if (window.ga && window.ga.getAll) {
    const trackers = window.ga
      .getAll()
      .filter((element) => element.get("trackingId") === nhsTrackingId);
    googleAnalyticsId = trackers[0].get("clientId");
  }

  return googleAnalyticsId;
}

function getGoogleAnalyticsUserId() {
  let googleAnalyticsUserId;

  if (window.ga && window.ga.getAll) {
    const trackers = window.ga
      .getAll()
      .filter((element) => element.get("trackingId") === nhsTrackingId);
    googleAnalyticsUserId = trackers[0].get("userId");
  }

  return googleAnalyticsUserId;
}

/**
 * @private getMarketSession: get the market format to database team in Google Analytics.
 *
 * @returns A string that contains {Market Name} - {Market Id}.
 */
function getMarketSession() {
  const marketId = getMarketId();
  const marketName = getMarketName();
  if (marketId && marketName) {
    return `${marketName} - ${marketId}`;
  }
  return "";
}

function getVisitedMpcIds() {
  return window.NewHomeSource.WebStorageHandler.getSessionStorageItem(
    "visitedMpcIds"
  );
}

/**
 * @private
 * getAmenities: get the value the amenities of dataLayer.
 *
 * @returns amenities value from dataLayer.
 */
function getAmenities() {
  const amenities =
    window.NewHomeSource.Modules.tagManager.getFromDataLayer("amenities");
  if (amenities instanceof Array) {
    return amenities.join(", ");
  }

  return amenities || "";
}

/**
 * @private
 * getLanguage: get the value the language of document language.
 *
 * @returns nhs language value from docuemnt.
 */
function getLanguage() {
  return document.documentElement.lang;
}

/**
 * @private
 * getReferCode: get the refer code from URL.
 *
 * @returns refer parameter in the URL.
 */
function getReferCode() {
  const params = new URLSearchParams(window.location.search);
  return params.get("refer");
}

/**
 * @private
 * getTrustBuilderReviews: get trust builder reviews value.
 *
 * @returns trust builder reviews value from dataLayer.
 */
function getTrustBuilderReviews() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "trustBuilderReviews"
  );
}

/**
 * @private
 * getUserCity: get city value from IP.
 *
 * @returns city value from dataLayer.
 */
function getUserCity() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("userCity");
}

/**
 * @private
 * getUserState: get state value from IP.
 *
 * @returns state value from dataLayer.
 */
function getUserState() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("userState");
}

/**
 * @private
 * getUserCountry: get country value from IP.
 *
 * @returns country value from dataLayer.
 */
function getUserCountry() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "userCountry"
  );
}

/**
 * @private
 * getUserMarketId: get market id from IP.
 *
 * @returns market id from dataLayer.
 */
function getUserMarketId() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "userMarketId"
  );
}

/**
 * @private
 * getUserStatus: get status of the user.
 *
 * @returns userStatus from dataLayer.
 */
function getUserStatus() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer("userStatus");
}

/**
 * @private
 * getUserZipCode: get zip code value from IP.
 *
 * @returns zip code value from dataLayer.
 */
function getUserZipCode() {
  return window.NewHomeSource.Modules.tagManager.getFromDataLayer(
    "userZipCode"
  );
}


function getAnalyticsTrackingProperties() {
  window.NewHomeSource = window.NewHomeSource || {};

  if (
    !window.NewHomeSource.Modules.tagManager ||
    !window.NewHomeSource.Modules.cookieHandler ||
    !window.NewHomeSource.Modules.IdleValue
  ) {
    return {};
  }

  const globalValues = {
    abTestsOnThisPage: getAbTestsOnThePage(),
    time: getCentralStandardTime(),
    brand_id: getBrandId(),
    brand_name: getBrandName(),
    brand_partner_name: getBrandPartnerName(),
    builder_id: getBuilderId(),
    builder_division: getBuilderDivision(),
    builder_name: getBuilderName(),
    city: getCity(),
    county: getCounty(),
    codeBuildeVersion: getCodeBuildVersion(),
    community_high_price: getHighPrice(),
    community_low_price: getLowPrice(),
    community_name: getCommunityName(),
    communityId: getCommunityId(),
    communityType: getCommunityType(),
    current_page_number: getCurrentPageNumber(),
    filters_applied: getFiltersApplied(),
    googleAnalyticsClientId: getGoogleAnalyticsClientId(),
    googleAnalyticsUserId: getGoogleAnalyticsUserId(),
    location: getLocation(),
    hasIfp: hasIfp(),
    hasIsp: hasIsp(),
    home_price: getHomePrice(),
    home_type: getHomeType(),
    isLimitedAvailability: isLimitedAvailability(),
    isPriced: isPriced(),
    i_uid: getIUid(),
    marketId: getMarketId(),
    marketName: getMarketName(),
    segmentAnonymousId: getSegmentAnonymousId(),
    marketSession: getMarketSession(),
    page_type: getPageType(),
    partner_id: getPartnerId(),
    partner_name: getPartnerName(),
    price_reduction: getPriceReduction(),
    brandPartnerNamePartnerName: getBrandPartnerNamePartnerName(),
    offMarket: getOffMarket(),
    plan_id: getPlanId(),
    plan_name: getPlanName(),
    primary_image_url: getPrimaryImageUrl(),
    referCode: getReferCode(),
    response_version: getresponseVersion(),
    result_count: getResultCount(),
    spec_id: getSpecId(),
    state: getState(),
    userAgent: navigator.userAgent,
    userId: getUserId(),
    visitedMpcIds: getVisitedMpcIds(),
    zip: getZip(),
    amenities: getAmenities(),
    language: getLanguage(),
    trustBuilderReviews: getTrustBuilderReviews(),
    userCity: getUserCity(),
    userState: getUserState(),
    userCountry: getUserCountry(),
    userMarketId: getUserMarketId(),
    userStatus: getUserStatus(),
    userZipCode: getUserZipCode()
  }; 

  return cleanObjectsEmptyProperties(globalValues);
}

export default getAnalyticsTrackingProperties;
